import React, { useEffect, useState } from "react";
import {
  Button,
  Chip,
  CircularProgress,
  FormLabel,
  Grid,
  MenuItem,
  Paper,
  Select,
  TextField,
  Typography,
  Box,
  FormControl,
  Alert,
} from "@mui/material";
import LoadingButton from "@mui/lab/LoadingButton";
import Autocomplete from "@mui/material/Autocomplete";
import DeleteForeverIcon from "@mui/icons-material/DeleteForever";
import { useTranslation } from "react-i18next";
import useUpdate from "../../../../api/useUpdate";
import useFetchObjects from "../../../../api/useFetchObjects";
import useDelete from "../../../../api/useDelete";
import { useAuth } from "../../../../context";
import Type from "./Type";
import useFetchObject from "../../../../api/useFetchObject";

const EditHtPatientProcedure = ({ onClose, id }) => {
  const { t } = useTranslation();
  const auth = useAuth();
  const token = auth?.user?.token;

  // Local open state for Type/Period/Lab creation
  const [open, setOpen] = useState(false);
  const [typeDialog, setTypeDialog] = useState("");

  // ----------------------------
  // 1. Fetch data for the HT patient procedure
  // ----------------------------
  const { data, isLoading, isError } = useFetchObject(
    ["ht-patients"],
    "ht-patients",
    id,
    token
  );

  // Local formData state
  const [formData, setFormData] = useState({
    type: "",
    procedure_date: "",
    procedure_day: "",
    status: "",
    period: "",
    doctors: [], // array of doctor IDs
    lab: [], // array of lab IDs
    total_cost: "",
    advance: "",
    advance_currency: "",
    exchange_rate: "",
    currency: "",
    zone: "",
    description: "",
    patient: "",
    next_visit_days: "",
    next_visit_date: "",
  });

  // ----------------------------
  // 2. Update formData when fetched data is available
  // ----------------------------
  useEffect(() => {
    if (data) {
      const formattedProcedureDate = data.procedure_date
        ? new Date(data.procedure_date).toISOString().slice(0, 16)
        : "";
      setFormData({
        type: data.type || "",
        procedure_date: formattedProcedureDate,
        procedure_day: data.procedure_day || "",
        status: data.status || "",
        period: data.period || "",
        doctors: data.doctors || [],
        lab: data.lab || [],
        total_cost: data.total_cost || "",
        discount: data.discount || "",
        advance: data.advance || "",
        advance_currency: data.advance_currency || "",
        exchange_rate: data.exchange_rate || "",
        currency: data.currency || "",
        zone: data.zone || "",
        description: data.description || "",
        patient: data.patient || "",
        next_visit_days: data.next_visit_days || "",
        next_visit_date: data.next_visit_date || "",
      });
    }
  }, [data]);

  // ----------------------------
  // 3. Fetch required lists for Autocomplete/Select options
  // ----------------------------
  const [doctorsList, setDoctorsList] = useState([]); // all fetched doctors
  const [selectedDoctors, setSelectedDoctors] = useState([]); // current selection

  const {
    data: users,
    isLoading: isLoadingUser,
    isError: isErrorUser,
  } = useFetchObjects(["users"], "users/?position=7", token);

  const {
    data: types,
    isLoading: isLoadingType,
    isError: isErrorType,
  } = useFetchObjects(["procedure-type"], `procedure-type/?type=1`, token);

  const {
    data: patients,
    isLoading: isLoadingPatient,
    isError: isErrorPatient,
  } = useFetchObjects(["patients"], "patients/?page_size=200", token);

  const {
    data: periods,
    isLoading: isLoadingPeriod,
    isError: isErrorPeriod,
  } = useFetchObjects(["period"], "period", token);

  const {
    data: labs,
    isLoading: isLoadingLab,
    isError: isErrorLab,
  } = useFetchObjects(["lab"], "lab", token);

  // Store fetched doctors in state
  useEffect(() => {
    if (users) setDoctorsList(users);
  }, [users]);

  // Keep selectedDoctors in sync with formData.doctors (IDs)
  useEffect(() => {
    const newSelection = doctorsList.filter((doc) =>
      formData.doctors.includes(doc.id)
    );
    setSelectedDoctors(newSelection);
  }, [formData.doctors, doctorsList]);

  // ----------------------------
  // 4. Hooks for update and deletion (Type/Period/Lab)
  // ----------------------------
  const {
    handleUpdate,
    loading: loadingEdit,
    success,
  } = useUpdate("ht-patients", token);

  const { handleDelete, ConfirmDialog } = useDelete("procedure-type", token);
  const {
    handleDelete: handleDeletePeriod,
    ConfirmDialog: PeriodConfirmDialog,
  } = useDelete("period", token);
  const { handleDelete: handleDeleteLab, ConfirmDialog: LabConfirmDialog } =
    useDelete("lab", token);

  // ----------------------------
  // 5. Handlers for input and Autocomplete changes
  // ----------------------------
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleDoctorsChange = (event, newValue) => {
    setSelectedDoctors(newValue);
    const docIds = newValue.map((doc) => doc.id);
    setFormData((prev) => ({ ...prev, doctors: docIds }));
  };

  // For labs, since using a multiple Select with chips, remove a lab by ID
  const handleDeleteChip = (labId) => {
    setFormData((prev) => ({
      ...prev,
      lab: prev.lab.filter((value) => value !== labId),
    }));
  };

  // Toggle the dialog for adding new type/period/lab
  const handleOpenDialog = () => {
    setOpen(!open);
  };

  // ----------------------------
  // 6. Auto-calculate next_visit_date
  // ----------------------------
  useEffect(() => {
    if (formData.next_visit_days) {
      const daysToAdd = parseInt(formData.next_visit_days, 10);
      if (!isNaN(daysToAdd)) {
        const baseDate = formData.procedure_date
          ? new Date(formData.procedure_date)
          : new Date();
        baseDate.setDate(baseDate.getDate() + daysToAdd);
        const year = baseDate.getFullYear();
        const month = ("0" + (baseDate.getMonth() + 1)).slice(-2);
        const day = ("0" + baseDate.getDate()).slice(-2);
        const formattedDate = `${year}-${month}-${day}`;
        setFormData((prev) => ({
          ...prev,
          next_visit_date: formattedDate,
        }));
      }
    }
  }, [formData.procedure_date, formData.next_visit_days]);

  useEffect(() => {
    if (success) {
      onClose();
    }
  }, [success]);

  // ----------------------------
  // 7. Submit update request
  // ----------------------------
  const handleSubmit = (e) => {
    e.preventDefault();
    if (!data) return;

    const submitData = new FormData();
    submitData.append("type", formData.type);
    if (formData.procedure_date) {
      submitData.append("procedure_date", formData.procedure_date);
    }
    submitData.append("procedure_day", formData.procedure_day);
    submitData.append("status", formData.status);
    submitData.append("period", formData.period);
    submitData.append("total_cost", formData.total_cost);
    submitData.append("discount", formData.discount);
    submitData.append("advance", formData.advance);
    submitData.append("advance_currency", formData.advance_currency);
    submitData.append("exchange_rate", formData.exchange_rate);
    submitData.append("currency", formData.currency);
    submitData.append("zone", formData.zone);
    submitData.append("description", formData.description);
    submitData.append("patient", formData.patient);
    submitData.append("next_visit_date", formData.next_visit_date);
    submitData.append("next_visit_days", formData.next_visit_days);

    // Append each selected doctor ID
    formData.doctors.forEach((docId) => {
      submitData.append("doctors", docId);
    });

    // Append each lab ID
    formData.lab.forEach((labId) => {
      submitData.append("lab", labId);
    });

    // Call update and then close the dialog
    handleUpdate(data?.id, submitData);
  };

  if (isError) {
    return (
      <Grid container justifyContent={"center"} mt={4}>
        <Alert severity="error">Failed to load page</Alert>
      </Grid>
    );
  }

  if (isLoading) {
    return (
      <Grid container justifyContent={"center"} mt={4} padding={"20px"}>
        <CircularProgress />
      </Grid>
    );
  }

  return (
    <Grid container justifyContent="center">
      <Grid item xs={11.5} mt={2}>
        <Paper
          sx={{ padding: "20px", borderRadius: "10px", boxShadow: 3 }}
          component="form"
          onSubmit={handleSubmit}
        >
          <Typography
            variant="h5"
            gutterBottom
            fontWeight="bold"
            align="center"
            sx={{ borderBottom: "3px solid #007471", pb: 1.5 }}
          >
            {t("call_center.editHtPatient")}
          </Typography>

          <Grid container spacing={3} mt={1}>
            {/* PATIENT */}
            <Grid item xs={12} md={4}>
              <FormLabel>{t("call_center.patient")}</FormLabel>
              {isLoadingPatient ? (
                <Grid container justifyContent="center" padding="10px">
                  <CircularProgress />
                </Grid>
              ) : isErrorPatient ? (
                <Typography color="error">
                  {t("call_center.errorField")}
                </Typography>
              ) : (
                <Autocomplete
                  options={patients?.results || []}
                  value={
                    patients?.results?.find(
                      (p) => p.id === formData?.patient
                    ) || null
                  }
                  onChange={(event, newValue) => {
                    setFormData((prev) => ({
                      ...prev,
                      patient: newValue ? newValue.id : "",
                    }));
                  }}
                  filterOptions={(options, state) => {
                    const inputValue = state.inputValue.trim().toLowerCase();
                    return options.filter((option) => {
                      const fullName = `${option.firstname} ${
                        option.lastname || ""
                      }`.toLowerCase();
                      const phone = (option.phone || "").toLowerCase();
                      if (/^\d+$/.test(inputValue)) {
                        return phone === inputValue;
                      }
                      return fullName.includes(inputValue);
                    });
                  }}
                  getOptionLabel={(option) =>
                    `${option.firstname} ${option.lastname} - ${option.phone}`
                  }
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      variant="outlined"
                      placeholder={t("call_center.patient")}
                    />
                  )}
                />
              )}
            </Grid>

            {/* TYPE */}
            <Grid item xs={12} md={4}>
              <FormLabel>{t("call_center.type")}</FormLabel>
              <TextField
                select
                fullWidth
                variant="outlined"
                name="type"
                value={formData.type}
                onChange={handleInputChange}
              >
                <Button
                  fullWidth
                  variant="outlined"
                  sx={{ textTransform: "capitalize" }}
                  onClick={() => {
                    setTypeDialog("type");
                    handleOpenDialog();
                  }}
                >
                  {t("call_center.addNewType")}
                </Button>
                {isLoadingType && (
                  <Grid container justifyContent="center" padding="10px">
                    <CircularProgress />
                  </Grid>
                )}
                {isErrorType && (
                  <Typography color="error">
                    {t("call_center.errorField")}
                  </Typography>
                )}
                {types?.map((item) => (
                  <MenuItem value={item.id} key={item.id}>
                    <Box
                      display="flex"
                      justifyContent="space-between"
                      alignItems="center"
                      width="100%"
                    >
                      <Typography>{item.name}</Typography>
                      {formData.type !== item.id && (
                        <DeleteForeverIcon
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDelete(item.id);
                          }}
                          sx={{ color: "red" }}
                        />
                      )}
                    </Box>
                  </MenuItem>
                ))}
              </TextField>
            </Grid>

            {/* PROCEDURE DAY */}
            <Grid item xs={12} md={4}>
              <FormControl fullWidth variant="outlined">
                <FormLabel>{t("call_center.procedureDay")}</FormLabel>
                <Select
                  labelId="procedure_date-label"
                  id="procedure_date"
                  name="procedure_day"
                  value={formData?.procedure_day || ""}
                  onChange={handleInputChange}
                  label={t("call_center.procedureDay")}
                >
                  <MenuItem value="monday">{t("call_center.monday")}</MenuItem>
                  <MenuItem value="tuesday">
                    {t("call_center.tuesday")}
                  </MenuItem>
                  <MenuItem value="wednesday">
                    {t("call_center.wednesday")}
                  </MenuItem>
                  <MenuItem value="thursday">
                    {t("call_center.thursday")}
                  </MenuItem>
                  <MenuItem value="friday">{t("call_center.friday")}</MenuItem>
                  <MenuItem value="saturday">
                    {t("call_center.saturday")}
                  </MenuItem>
                  <MenuItem value="sunday">{t("call_center.sunday")}</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            {/* PROCEDURE DATE */}
            <Grid item xs={12} md={4}>
              <FormLabel>{t("call_center.procedureDate")}</FormLabel>
              <TextField
                fullWidth
                type="datetime-local"
                variant="outlined"
                name="procedure_date"
                value={formData.procedure_date}
                onChange={handleInputChange}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>

            {/* STATUS */}
            <Grid item xs={12} md={4}>
              <FormLabel>{t("call_center.status")}</FormLabel>
              <TextField
                select
                fullWidth
                variant="outlined"
                name="status"
                value={formData.status}
                onChange={handleInputChange}
              >
                <MenuItem value={1}>{t("call_center.done")}</MenuItem>
                <MenuItem value={2}>{t("call_center.cancel")}</MenuItem>
                <MenuItem value={3}>{t("call_center.followUP")}</MenuItem>
                <MenuItem value={4}>{t("call_center.inProgress")}</MenuItem>
                <MenuItem value={5}>{t("call_center.dontWant")}</MenuItem>
                <MenuItem value={6}>{t("call_center.nextDate")}</MenuItem>
                <MenuItem value={7}>{t("call_center.pending")}</MenuItem>
                <MenuItem value={8}>{t("call_center.fixed")}</MenuItem>
                <MenuItem value={9}>{t("call_center.flexible")}</MenuItem>
                <MenuItem value={10}>{t("call_center.noAnswer")}</MenuItem>
                <MenuItem value={11}>{t("call_center.information")}</MenuItem>
                <MenuItem value={12}>{t("call_center.comingSoon")}</MenuItem>
              </TextField>
            </Grid>

            {/* PERIOD */}
            <Grid item xs={12} md={4}>
              <FormLabel>{t("call_center.period")}</FormLabel>
              <TextField
                select
                fullWidth
                variant="outlined"
                name="period"
                value={formData.period}
                onChange={handleInputChange}
              >
                <Button
                  fullWidth
                  variant="outlined"
                  sx={{ textTransform: "capitalize" }}
                  onClick={() => {
                    setTypeDialog("period");
                    handleOpenDialog();
                  }}
                >
                  {t("call_center.addNewPeriod")}
                </Button>
                {isLoadingPeriod && (
                  <Grid container justifyContent="center" padding="10px">
                    <CircularProgress />
                  </Grid>
                )}
                {isErrorPeriod && (
                  <Typography color="error">
                    {t("call_center.errorField")}
                  </Typography>
                )}
                {periods?.map((item) => (
                  <MenuItem value={item.id} key={item.id}>
                    <Box
                      display="flex"
                      justifyContent="space-between"
                      alignItems="center"
                      width="100%"
                    >
                      <Typography>{item.name}</Typography>
                      {formData.period !== item.id && (
                        <DeleteForeverIcon
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDeletePeriod(item.id);
                          }}
                          sx={{ color: "red" }}
                        />
                      )}
                    </Box>
                  </MenuItem>
                ))}
              </TextField>
            </Grid>

            {/* DOCTORS (Autocomplete, multiple) */}
            <Grid item xs={12} md={4}>
              <FormLabel>{t("call_center.doctors")}</FormLabel>
              {isLoadingUser ? (
                <CircularProgress size={24} sx={{ mt: 1 }} />
              ) : isErrorUser ? (
                <Typography color="error">
                  {t("call_center.errorField")}
                </Typography>
              ) : (
                <Autocomplete
                  multiple
                  options={doctorsList}
                  value={selectedDoctors}
                  onChange={handleDoctorsChange}
                  getOptionLabel={(option) =>
                    `${option.first_name} ${option.last_name}` ||
                    "Unknown Doctor"
                  }
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      variant="outlined"
                      placeholder={t("call_center.selectDoctors")}
                    />
                  )}
                />
              )}
            </Grid>

            {/* LABS (Multiple Select with chips) */}
            <Grid item xs={12} md={4}>
              <FormLabel>{t("call_center.lab")}</FormLabel>
              <Select
                multiple
                fullWidth
                variant="outlined"
                name="lab"
                value={formData.lab}
                onChange={handleInputChange}
                renderValue={(selected) => (
                  <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                    {selected.map((labId) => {
                      const foundLab = labs?.find((l) => l.id === labId);
                      return (
                        <Chip
                          key={labId}
                          label={foundLab ? foundLab.name : labId}
                          onDelete={(e) => {
                            e.stopPropagation();
                            handleDeleteChip(labId);
                          }}
                        />
                      );
                    })}
                  </Box>
                )}
              >
                <Button
                  fullWidth
                  variant="outlined"
                  sx={{ textTransform: "capitalize" }}
                  onClick={() => {
                    setTypeDialog("lab");
                    handleOpenDialog();
                  }}
                >
                  {t("call_center.addNewLab")}
                </Button>
                {isLoadingLab && (
                  <Grid container justifyContent="center" padding="10px">
                    <CircularProgress />
                  </Grid>
                )}
                {isErrorLab && (
                  <Typography color="error">
                    {t("call_center.errorField")}
                  </Typography>
                )}
                {labs?.map((item) => (
                  <MenuItem value={item.id} key={item.id}>
                    <Box
                      display="flex"
                      justifyContent="space-between"
                      alignItems="center"
                      width="100%"
                    >
                      <Typography>{item.name}</Typography>
                      {formData.lab.indexOf(item.id) === -1 && (
                        <DeleteForeverIcon
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDeleteLab(item.id);
                          }}
                          sx={{ color: "red" }}
                        />
                      )}
                    </Box>
                  </MenuItem>
                ))}
              </Select>
            </Grid>

            {/* ZONE */}
            <Grid item xs={6} md={4}>
              <FormLabel>{t("call_center.zone")}</FormLabel>
              <TextField
                fullWidth
                variant="outlined"
                name="zone"
                value={formData.zone}
                onChange={handleInputChange}
              />
            </Grid>

            {/* TOTAL PRICE */}
            <Grid item xs={6} md={4}>
              <FormLabel>{t("call_center.totalPrice")}</FormLabel>
              <TextField
                fullWidth
                type="number"
                variant="outlined"
                name="total_cost"
                value={formData.total_cost}
                onChange={handleInputChange}
              />
            </Grid>

            {/* CURRENCY */}
            <Grid item xs={6} md={4}>
              <FormLabel>{t("call_center.currency")}</FormLabel>
              <TextField
                select
                fullWidth
                variant="outlined"
                name="currency"
                value={formData.currency}
                onChange={handleInputChange}
              >
                <MenuItem value={1}>{t("call_center.afn")}</MenuItem>
                <MenuItem value={2}>{t("call_center.usd")}</MenuItem>
                <MenuItem value={3}>{t("call_center.eru")}</MenuItem>
              </TextField>
            </Grid>

            {/* ADVANCE */}
            <Grid item xs={6} md={4}>
              <FormLabel>{t("call_center.advance")}</FormLabel>
              <TextField
                fullWidth
                type="number"
                variant="outlined"
                name="advance"
                value={formData.advance}
                onChange={handleInputChange}
              />
            </Grid>

            {/* ADVANCE CURRENCY */}
            <Grid item xs={6} md={4}>
              <FormLabel>
                {t("call_center.advanceCurrency", "Advance Currency")}
              </FormLabel>
              <TextField
                select
                fullWidth
                variant="outlined"
                name="advance_currency"
                value={formData.advance_currency}
                onChange={handleInputChange}
              >
                <MenuItem value={1}>{t("call_center.afn")}</MenuItem>
                <MenuItem value={2}>{t("call_center.usd")}</MenuItem>
                <MenuItem value={3}>{t("call_center.eru")}</MenuItem>
              </TextField>
            </Grid>

            {/* TOTAL PRICE */}
            <Grid item xs={6} md={4}>
              <FormLabel>{t("call_center.discount")} (%)</FormLabel>
              <TextField
                fullWidth
                type="number"
                variant="outlined"
                name="discount"
                value={formData.discount}
                onChange={handleInputChange}
              />
            </Grid>

            {/* EXCHANGE RATE */}
            <Grid item xs={6} md={4}>
              <FormLabel>{t("call_center.exchangeRate")}</FormLabel>
              <TextField
                fullWidth
                type="number"
                variant="outlined"
                name="exchange_rate"
                value={formData.exchange_rate}
                onChange={handleInputChange}
              />
            </Grid>

            {/* NEXT VISIT DAYS */}
            <Grid item xs={6} md={4}>
              <FormLabel>{t("call_center.nextVisitDays")}</FormLabel>
              <TextField
                fullWidth
                type="number"
                variant="outlined"
                name="next_visit_days"
                value={formData.next_visit_days}
                onChange={handleInputChange}
              />
            </Grid>

            {/* NEXT VISIT DATE */}
            <Grid item xs={6} md={4}>
              <FormLabel>{t("call_center.nextVisitDate")}</FormLabel>
              <TextField
                fullWidth
                type="date"
                variant="outlined"
                name="next_visit_date"
                value={formData.next_visit_date}
                onChange={handleInputChange}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>

            {/* DESCRIPTION */}
            <Grid item xs={6} md={4}>
              <FormLabel>
                {t("call_center.description", "Description")}
              </FormLabel>
              <TextField
                fullWidth
                variant="outlined"
                name="description"
                value={formData?.description}
                onChange={handleInputChange}
                InputLabelProps={{
                  shrink: true,
                }}
              />
            </Grid>

            {/* SUBMIT BUTTON */}
            <Grid item xs={12} textAlign="end">
              <LoadingButton
                loading={loadingEdit}
                variant="contained"
                color="success"
                type="submit"
                sx={{ padding: "10px 20px" }}
              >
                {t("call_center.save")}
              </LoadingButton>
            </Grid>
          </Grid>
        </Paper>
      </Grid>

      {/* Confirm deletion dialogs for type/period/lab */}
      <ConfirmDialog />
      <LabConfirmDialog />
      <PeriodConfirmDialog />

      {/* Dialog for adding new type/period/lab */}
      <Type
        open={open}
        onClose={handleOpenDialog}
        token={token}
        type={typeDialog}
      />
    </Grid>
  );
};

export default EditHtPatientProcedure;
